import { useState, useEffect, useCallback } from 'react';
import { getMyProfile } from '@/api/auth';
import type {
  AccountStatus,
  AuthPayload,
  MembershipTier,
  UserRole,
} from '@/api/auth';
import {
  broadcastCurrentAuthState,
  clearAuthSession,
  clearStoredAuthUser,
  getAccessToken,
  getStoredAuthUser,
  requestAuthSessionSync,
  persistAuthSession,
  subscribeAuthSync,
  storeAuthUser,
} from '@/utils/authStorage';

export interface AuthUser {
  id?: number;
  username?: string | null;
  email?: string;
  name?: string;
  role: UserRole;
  roles?: string[];
  daohao?: string | null;
  namePublic?: boolean;
  notifyNewSolution?: boolean;
  notifyToolUpdate?: boolean;
  notifyCommunity?: boolean;
  membershipTier?: MembershipTier;
  accountStatus?: AccountStatus;
  isEmailVerified?: boolean;
  company?: string | null;
  position?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  lastLoginAt?: string | null;
  createdAt?: string;
}

function normalizeUser(user: AuthUser): AuthUser {
  const role: UserRole =
    user.role === 'super_admin' || user.role === 'admin' ? user.role : 'member';
  const username = user.username || user.phone || user.email || '';

  return {
    ...user,
    username,
    role,
  };
}

let authProfileBootstrapPromise: Promise<AuthUser | null> | null = null;
let authSessionSyncRequested = false;

function requestAuthSessionSyncOnce() {
  if (authSessionSyncRequested) return;
  authSessionSyncRequested = true;
  requestAuthSessionSync();
}

async function bootstrapAuthProfile(): Promise<AuthUser | null> {
  const hasAccessToken = Boolean(getAccessToken());
  const storedUser = getStoredUser();
  if (!hasAccessToken || !storedUser) {
    return null;
  }

  if (!authProfileBootstrapPromise) {
    authProfileBootstrapPromise = getMyProfile()
      .then((profile) => {
        if (!getAccessToken()) {
          return null;
        }
        if (profile.accountStatus !== 'active') {
          clearSession();
          return null;
        }
        const normalized = normalizeUser({
          ...profile,
          name: profile.name,
          username: profile.username || profile.phone || profile.email,
        });
        storeUser(normalized);
        broadcastCurrentAuthState();
        return normalized;
      })
      .catch((err: unknown) => {
        if (!getAccessToken()) {
          return null;
        }
        const code = typeof (err as { code?: unknown })?.code === 'number'
          ? Number((err as { code: number }).code)
          : null;
        const status = typeof (err as { httpStatus?: unknown })?.httpStatus === 'number'
          ? Number((err as { httpStatus: number }).httpStatus)
          : null;
        if (code === 40100 || code === 40310 || status === 401 || status === 403) {
          clearSession();
        }
        return null;
      })
      .finally(() => {
        authProfileBootstrapPromise = null;
      });
  }

  return authProfileBootstrapPromise;
}

export function getStoredUser(): AuthUser | null {
  try {
    const raw = getStoredAuthUser<AuthUser>();
    if (!raw) return null;
    return normalizeUser(raw);
  } catch {
    return null;
  }
}

export function storeUser(user: AuthUser): void {
  storeAuthUser(normalizeUser(user));
}

export function clearUser(): void {
  clearStoredAuthUser();
}

function persistSession(payload: AuthPayload) {
  persistAuthSession({
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    tokenType: payload.tokenType,
    accessExpiresIn: payload.accessExpiresIn,
    refreshExpiresIn: payload.refreshExpiresIn,
    user: {
      ...payload.user,
      username: payload.user.username || payload.user.phone || payload.user.email,
    },
  });
}

function clearSession() {
  clearAuthSession();
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);
  const [isReady, setIsReady] = useState(() => Boolean(getAccessToken() && getStoredUser()));

  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeAuthSync((message) => {
      if (!active) return;

      if (message.action === 'logout') {
        authSessionSyncRequested = false;
        setUser(null);
        setIsReady(true);
        return;
      }

      if (message.action === 'state' && message.snapshot?.user) {
        authSessionSyncRequested = false;
        const snapshotUser = message.snapshot.user as AuthUser;
        const syncedUser = normalizeUser({
          ...snapshotUser,
          username: snapshotUser.username || snapshotUser.phone || snapshotUser.email,
        });
        setUser(syncedUser);
        setIsReady(true);
      }
    });

    const hasAccessToken = Boolean(getAccessToken());
    const storedUser = getStoredUser();
    if (!hasAccessToken || !storedUser) {
      requestAuthSessionSyncOnce();
      const readyTimer = window.setTimeout(() => {
        if (active) {
          setIsReady(true);
        }
      }, 350);

      return () => {
        active = false;
        unsubscribe();
        window.clearTimeout(readyTimer);
      };
    }

    bootstrapAuthProfile()
      .then((profile) => {
        if (!active) return;
        authSessionSyncRequested = false;
        if (!profile) {
          setUser(null);
          return;
        }
        setUser(profile);
      })
      .catch(() => {
        if (!active) return;
        authSessionSyncRequested = false;
        setUser(null);
      })
      .finally(() => {
        if (active) {
          setIsReady(true);
        }
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const login = useCallback((userData: AuthUser) => {
    const normalized = normalizeUser(userData);
    authSessionSyncRequested = false;
    storeUser(normalized);
    broadcastCurrentAuthState();
    setUser(normalized);
    setIsReady(true);
  }, []);

  const setSession = useCallback((authPayload: AuthPayload) => {
    authSessionSyncRequested = false;
    persistSession(authPayload);
    setUser(
      normalizeUser({
        ...authPayload.user,
        username: authPayload.user.username || authPayload.user.phone || authPayload.user.email,
      }),
    );
    setIsReady(true);
  }, []);

  const logout = useCallback(() => {
    authSessionSyncRequested = false;
    clearSession();
    setUser(null);
    setIsReady(true);
  }, []);

  return {
    user,
    login,
    setSession,
    logout,
    isLoggedIn: !!user,
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin',
    isSuperAdmin: user?.role === 'super_admin',
    isReady,
  };
}
